import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, BookOpen, Shield, BarChart3, Brain, Target, 
  Building2, FileText, ChevronDown, Sparkles, MessageSquare,
  TrendingUp, CheckCircle, Zap, Globe, Lock, Search, Layers
} from "lucide-react";
import { Button } from "./ui/button";
import { trackLandingView } from "../utils/analytics";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/o8ge27c7_Logo%20Documatch%203.0.GIF";

const detectLang = () => {
  const saved = localStorage.getItem("documatch_language");
  if (saved && ["fr", "es", "en"].includes(saved)) return saved;
  const bl = (navigator.language || "").toLowerCase();
  if (bl.startsWith("es")) return "es";
  if (bl.startsWith("fr")) return "fr";
  return "en";
};

const content = {
  fr: {
    badge: "LIVRE BLANC 2026",
    title: "La transformation documentaire des entreprises",
    titleAccent: "à l'ère de l'IA",
    nav: "Retour à Documatch",
    toc: "Sommaire",
    cta1: "Obtenir une comparaison personnalisée",
    cta2: "Parler avec un expert",
    cta3: "Lancer le comparateur IA",
    sections: [
      {
        id: "resume",
        icon: BookOpen,
        title: "Résumé exécutif",
        content: "La gestion documentaire est devenue un enjeu critique de performance, de conformité et de compétitivité. Les organisations qui structurent leur stratégie GED constatent en moyenne :",
        stats: [
          { value: "-40%", label: "temps de recherche documentaire", color: "text-red-500" },
          { value: "-60%", label: "coûts liés à la gestion papier", color: "text-red-500" },
          { value: "+80%", label: "automatisation des processus", color: "text-green-500" },
          { value: "5-6 mois", label: "ROI moyen atteint", color: "text-blue-500" }
        ],
        conclusion: "Dans un contexte marqué par la facturation électronique, le RGPD et l'IA documentaire, la GED n'est plus une option opérationnelle mais une infrastructure stratégique."
      },
      {
        id: "strategique",
        icon: Target,
        title: "01. La GED comme infrastructure stratégique",
        content: "La Gestion Électronique des Documents ne se limite plus à la dématérialisation. Elle constitue désormais :",
        bullets: [
          "Un socle de gouvernance de l'information",
          "Un levier de productivité organisationnelle",
          "Un outil de conformité réglementaire",
          "Une base d'automatisation des processus métiers"
        ],
        highlight: "Les organisations perdent en moyenne 20% de leur temps à rechercher de l'information. La GED répond à un problème structurel : la fragmentation de l'information."
      },
      {
        id: "conformite",
        icon: Shield,
        title: "02. Conformité RGPD et facturation électronique",
        content: "La pression réglementaire impose une transformation rapide des systèmes documentaires.",
        subsections: [
          {
            subtitle: "RGPD — exigences clés",
            items: ["Traçabilité complète des accès", "Gestion des droits utilisateurs", "Chiffrement des données sensibles", "Politique de conservation structurée", "Journalisation des actions"]
          },
          {
            subtitle: "Facturation électronique",
            items: ["Formats structurés (Factur-X / ZUGFeRD)", "Archivage à valeur probante", "Intégration avec plateformes certifiées", "Automatisation des flux comptables"]
          }
        ],
        conclusion: "La GED devient une infrastructure de conformité financière."
      },
      {
        id: "evaluation",
        icon: BarChart3,
        title: "03. Évaluation des solutions GED",
        content: "Les solutions GED se différencient selon 4 niveaux de maturité :",
        table: {
          headers: ["Capacité", "Basique", "Avancé", "Premium"],
          rows: [
            ["Archivage", "Standard", "Structuré", "Probant"],
            ["Recherche", "Mots-clés", "Full-text", "Sémantique"],
            ["Automatisation", "Manuelle", "Règles", "IA adaptative"],
            ["Sécurité", "Mot de passe", "RBAC", "Zero Trust"],
            ["IA documentaire", "Absente", "OCR", "Classification + extraction"],
            ["Intégrations", "Export", "API", "Connecteurs natifs"]
          ]
        },
        conclusion: "La valeur d'une GED réside dans son niveau d'intelligence, pas seulement de stockage."
      },
      {
        id: "cas",
        icon: Building2,
        title: "04. Cas d'usage structurants",
        cases: [
          {
            name: "PME (50 collaborateurs)",
            before: ["Documents dispersés", "Perte de temps importante", "Risque de perte d'information"],
            after: ["Centralisation complète", "Recherche instantanée", "Sauvegarde automatisée"]
          },
          {
            name: "Organisation multi-sites (500 collaborateurs)",
            before: ["Silos d'information", "Incohérences de versions", "Non-conformité RGPD"],
            after: ["Plateforme unifiée", "Gouvernance centralisée", "Conformité assurée"]
          },
          {
            name: "Finance & RH",
            before: ["Traitement manuel des factures (5 jours)", "Archivage papier", "Erreurs humaines fréquentes"],
            after: ["Traitement en 24h", "Coffre-fort numérique", "Extraction automatisée"]
          }
        ]
      },
      {
        id: "ia",
        icon: Brain,
        title: "05. IA documentaire : nouveau levier de performance",
        content: "L'intelligence artificielle transforme profondément la GED.",
        capabilities: [
          { title: "Classification automatique", desc: "Identification et tri des documents sans intervention humaine" },
          { title: "Extraction intelligente", desc: "Lecture automatique des données critiques (dates, montants, références)" },
          { title: "Anticipation des événements", desc: "Détection d'anomalies et alertes proactives" }
        ],
        impact: ["-80% de tâches manuelles", "Précision accrue des processus", "Réduction des erreurs opérationnelles"]
      },
      {
        id: "roi",
        icon: TrendingUp,
        title: "06. ROI et performance",
        content: "La GED génère des impacts mesurables rapides.",
        timeline: [
          { period: "Mois 1–3", label: "Phase d'adoption", value: "-15%" },
          { period: "Mois 4–6", label: "Gain de productivité", value: "+25%" },
          { period: "Mois 7–12", label: "Automatisation avancée", value: "+65%" },
          { period: "Année 2–3", label: "Maturité organisationnelle", value: "+120%" }
        ],
        kpis: [
          { value: "-40%", label: "Temps de recherche" },
          { value: "-60%", label: "Coûts opérationnels" },
          { value: "80%", label: "Automatisation" },
          { value: "95%+", label: "Conformité RGPD" }
        ]
      },
      {
        id: "decision",
        icon: Sparkles,
        title: "07. Cadre de décision",
        criteria: [
          { icon: Layers, title: "Scalabilité", desc: "Capacité à évoluer avec l'organisation" },
          { icon: Zap, title: "Intégration", desc: "Compatibilité avec ERP / CRM existants" },
          { icon: Brain, title: "Intelligence", desc: "Niveau d'automatisation et d'IA embarquée" },
          { icon: Lock, title: "Gouvernance", desc: "Sécurité, conformité et traçabilité" }
        ]
      }
    ],
    whyTitle: "08. Pourquoi Documatch",
    whyContent: "Documatch est un comparateur indépendant de solutions GED, Cloud et ERP.",
    whyPoints: [
      "Réduction du temps de sélection",
      "Comparaison objective des solutions",
      "Analyse des fonctionnalités critiques",
      "Recommandations adaptées au contexte"
    ],
    conclusionTitle: "Conclusion",
    conclusionText: "La GED n'est plus une technologie de support. Elle est devenue une infrastructure stratégique de performance, conformité et automatisation. Les organisations qui structurent leur gestion documentaire gagnent un avantage compétitif durable."
  },
  es: {
    badge: "LIBRO BLANCO 2026",
    title: "La transformación documental de las empresas",
    titleAccent: "en la era de la IA",
    nav: "Volver a Documatch",
    toc: "Índice",
    cta1: "Obtener comparativa personalizada",
    cta2: "Hablar con un experto",
    cta3: "Lanzar el comparador IA",
    sections: [
      {
        id: "resume",
        icon: BookOpen,
        title: "Resumen ejecutivo",
        content: "La gestión documental se ha convertido en un factor crítico de rendimiento, cumplimiento y competitividad. Las organizaciones que estructuran su estrategia DMS observan en promedio:",
        stats: [
          { value: "-40%", label: "tiempo de búsqueda documental", color: "text-red-500" },
          { value: "-60%", label: "costos de gestión en papel", color: "text-red-500" },
          { value: "+80%", label: "automatización de procesos", color: "text-green-500" },
          { value: "5-6 meses", label: "ROI medio alcanzado", color: "text-blue-500" }
        ],
        conclusion: "En un contexto marcado por la facturación electrónica, el RGPD y la IA documental, el DMS ya no es una opción operativa sino una infraestructura estratégica."
      },
      {
        id: "strategique", icon: Target, title: "01. El DMS como infraestructura estratégica",
        content: "La Gestión Electrónica de Documentos ya no se limita a la digitalización. Ahora constituye:",
        bullets: ["Una base de gobernanza de la información", "Una palanca de productividad organizacional", "Una herramienta de cumplimiento normativo", "Una base de automatización de procesos"],
        highlight: "Las organizaciones pierden en promedio un 20% de su tiempo buscando información. El DMS resuelve un problema estructural: la fragmentación de la información."
      },
      { id: "conformite", icon: Shield, title: "02. Cumplimiento RGPD y facturación electrónica", content: "La presión regulatoria impone una transformación rápida de los sistemas documentales.",
        subsections: [
          { subtitle: "RGPD — requisitos clave", items: ["Trazabilidad completa de accesos", "Gestión de derechos de usuarios", "Cifrado de datos sensibles", "Política de conservación estructurada", "Registro de acciones"] },
          { subtitle: "Facturación electrónica", items: ["Formatos estructurados (Factur-X / ZUGFeRD)", "Archivo con valor probatorio", "Integración con plataformas certificadas", "Automatización de flujos contables"] }
        ], conclusion: "El DMS se convierte en una infraestructura de cumplimiento financiero." },
      { id: "evaluation", icon: BarChart3, title: "03. Evaluación de soluciones DMS", content: "Las soluciones DMS se diferencian según 4 niveles de madurez:",
        table: { headers: ["Capacidad", "Básico", "Avanzado", "Premium"],
          rows: [["Archivo", "Estándar", "Estructurado", "Probatorio"],["Búsqueda", "Palabras clave", "Full-text", "Semántica"],["Automatización", "Manual", "Reglas", "IA adaptativa"],["Seguridad", "Contraseña", "RBAC", "Zero Trust"],["IA documental", "Ausente", "OCR", "Clasificación + extracción"],["Integraciones", "Exportación", "API", "Conectores nativos"]]
        }, conclusion: "El valor de un DMS reside en su nivel de inteligencia, no solo de almacenamiento." },
      { id: "cas", icon: Building2, title: "04. Casos de uso",
        cases: [
          { name: "PYME (50 empleados)", before: ["Documentos dispersos", "Pérdida de tiempo", "Riesgo de pérdida de información"], after: ["Centralización completa", "Búsqueda instantánea", "Copia de seguridad automatizada"] },
          { name: "Organización multi-sede (500 empleados)", before: ["Silos de información", "Incoherencias de versiones", "No conformidad RGPD"], after: ["Plataforma unificada", "Gobernanza centralizada", "Conformidad asegurada"] },
          { name: "Finanzas y RRHH", before: ["Procesamiento manual de facturas (5 días)", "Archivo en papel", "Errores humanos frecuentes"], after: ["Procesamiento en 24h", "Caja fuerte digital", "Extracción automatizada"] }
        ] },
      { id: "ia", icon: Brain, title: "05. IA documental: nueva palanca de rendimiento", content: "La inteligencia artificial transforma profundamente el DMS.",
        capabilities: [{ title: "Clasificación automática", desc: "Identificación y clasificación de documentos sin intervención humana" },{ title: "Extracción inteligente", desc: "Lectura automática de datos críticos (fechas, importes, referencias)" },{ title: "Anticipación de eventos", desc: "Detección de anomalías y alertas proactivas" }],
        impact: ["-80% de tareas manuales", "Mayor precisión en procesos", "Reducción de errores operativos"] },
      { id: "roi", icon: TrendingUp, title: "06. ROI y rendimiento", content: "El DMS genera impactos medibles rápidos.",
        timeline: [{ period: "Mes 1–3", label: "Fase de adopción", value: "-15%" },{ period: "Mes 4–6", label: "Ganancia de productividad", value: "+25%" },{ period: "Mes 7–12", label: "Automatización avanzada", value: "+65%" },{ period: "Año 2–3", label: "Madurez organizacional", value: "+120%" }],
        kpis: [{ value: "-40%", label: "Tiempo de búsqueda" },{ value: "-60%", label: "Costos operativos" },{ value: "80%", label: "Automatización" },{ value: "95%+", label: "Conformidad RGPD" }] },
      { id: "decision", icon: Sparkles, title: "07. Marco de decisión",
        criteria: [{ icon: Layers, title: "Escalabilidad", desc: "Capacidad de evolucionar con la organización" },{ icon: Zap, title: "Integración", desc: "Compatibilidad con ERP/CRM existentes" },{ icon: Brain, title: "Inteligencia", desc: "Nivel de automatización e IA integrada" },{ icon: Lock, title: "Gobernanza", desc: "Seguridad, conformidad y trazabilidad" }] }
    ],
    whyTitle: "08. Por qué Documatch",
    whyContent: "Documatch es un comparador independiente de soluciones DMS, Cloud y ERP.",
    whyPoints: ["Reducción del tiempo de selección", "Comparación objetiva de soluciones", "Análisis de funcionalidades críticas", "Recomendaciones adaptadas al contexto"],
    conclusionTitle: "Conclusión",
    conclusionText: "El DMS ya no es una tecnología de soporte. Se ha convertido en una infraestructura estratégica de rendimiento, cumplimiento y automatización. Las organizaciones que estructuran su gestión documental obtienen una ventaja competitiva duradera."
  },
  en: {
    badge: "WHITE PAPER 2026",
    title: "Enterprise document transformation",
    titleAccent: "in the age of AI",
    nav: "Back to Documatch",
    toc: "Table of Contents",
    cta1: "Get a personalized comparison",
    cta2: "Talk to an expert",
    cta3: "Launch the AI comparator",
    sections: [
      {
        id: "resume", icon: BookOpen, title: "Executive Summary",
        content: "Document management has become a critical factor for performance, compliance and competitiveness. Organizations that structure their DMS strategy see on average:",
        stats: [{ value: "-40%", label: "document search time", color: "text-red-500" },{ value: "-60%", label: "paper management costs", color: "text-red-500" },{ value: "+80%", label: "process automation", color: "text-green-500" },{ value: "5-6 months", label: "average ROI achieved", color: "text-blue-500" }],
        conclusion: "In a context marked by e-invoicing, GDPR and AI, DMS is no longer an operational option but a strategic infrastructure."
      },
      { id: "strategique", icon: Target, title: "01. DMS as Strategic Infrastructure",
        content: "Electronic Document Management is no longer limited to digitization. It now constitutes:",
        bullets: ["An information governance foundation", "An organizational productivity lever", "A regulatory compliance tool", "A business process automation base"],
        highlight: "Organizations lose on average 20% of their time searching for information. DMS addresses a structural problem: information fragmentation." },
      { id: "conformite", icon: Shield, title: "02. GDPR Compliance & E-Invoicing", content: "Regulatory pressure requires rapid transformation of document systems.",
        subsections: [{ subtitle: "GDPR — key requirements", items: ["Complete access traceability", "User rights management", "Sensitive data encryption", "Structured retention policy", "Action logging"] },{ subtitle: "E-Invoicing", items: ["Structured formats (Factur-X / ZUGFeRD)", "Evidential archiving", "Certified platform integration", "Automated accounting flows"] }],
        conclusion: "DMS becomes a financial compliance infrastructure." },
      { id: "evaluation", icon: BarChart3, title: "03. DMS Solution Evaluation", content: "DMS solutions differ across 4 maturity levels:",
        table: { headers: ["Capability", "Basic", "Advanced", "Premium"],
          rows: [["Archiving", "Standard", "Structured", "Evidential"],["Search", "Keywords", "Full-text", "Semantic"],["Automation", "Manual", "Rules", "Adaptive AI"],["Security", "Password", "RBAC", "Zero Trust"],["Document AI", "None", "OCR", "Classification + extraction"],["Integrations", "Export", "API", "Native connectors"]] },
        conclusion: "The value of a DMS lies in its intelligence level, not just storage." },
      { id: "cas", icon: Building2, title: "04. Use Cases",
        cases: [{ name: "SME (50 employees)", before: ["Scattered documents", "Significant time loss", "Information loss risk"], after: ["Complete centralization", "Instant search", "Automated backup"] },{ name: "Multi-site organization (500 employees)", before: ["Information silos", "Version inconsistencies", "GDPR non-compliance"], after: ["Unified platform", "Centralized governance", "Assured compliance"] },{ name: "Finance & HR", before: ["Manual invoice processing (5 days)", "Paper archiving", "Frequent human errors"], after: ["24h processing", "Digital vault", "Automated extraction"] }] },
      { id: "ia", icon: Brain, title: "05. Document AI: New Performance Lever", content: "Artificial intelligence is profoundly transforming DMS.",
        capabilities: [{ title: "Automatic classification", desc: "Document identification and sorting without human intervention" },{ title: "Intelligent extraction", desc: "Automatic reading of critical data (dates, amounts, references)" },{ title: "Event anticipation", desc: "Anomaly detection and proactive alerts" }],
        impact: ["-80% manual tasks", "Increased process accuracy", "Reduced operational errors"] },
      { id: "roi", icon: TrendingUp, title: "06. ROI & Performance", content: "DMS generates rapid measurable impacts.",
        timeline: [{ period: "Month 1–3", label: "Adoption phase", value: "-15%" },{ period: "Month 4–6", label: "Productivity gain", value: "+25%" },{ period: "Month 7–12", label: "Advanced automation", value: "+65%" },{ period: "Year 2–3", label: "Organizational maturity", value: "+120%" }],
        kpis: [{ value: "-40%", label: "Search time" },{ value: "-60%", label: "Operational costs" },{ value: "80%", label: "Automation" },{ value: "95%+", label: "GDPR compliance" }] },
      { id: "decision", icon: Sparkles, title: "07. Decision Framework",
        criteria: [{ icon: Layers, title: "Scalability", desc: "Ability to evolve with the organization" },{ icon: Zap, title: "Integration", desc: "Compatibility with existing ERP/CRM" },{ icon: Brain, title: "Intelligence", desc: "Automation and embedded AI level" },{ icon: Lock, title: "Governance", desc: "Security, compliance and traceability" }] }
    ],
    whyTitle: "08. Why Documatch",
    whyContent: "Documatch is an independent comparator of DMS, Cloud and ERP solutions.",
    whyPoints: ["Reduced selection time", "Objective solution comparison", "Critical feature analysis", "Context-adapted recommendations"],
    conclusionTitle: "Conclusion",
    conclusionText: "DMS is no longer a support technology. It has become a strategic infrastructure for performance, compliance and automation. Organizations that structure their document management gain a lasting competitive advantage."
  }
};

function Section({ children, className = "" }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`mb-16 ${className}`}
    >
      {children}
    </motion.section>
  );
}

export default function EBook() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(detectLang);
  const c = content[language];

  useEffect(() => {
    window.scrollTo(0, 0);
    trackLandingView("ebook");
  }, []);

  const langOptions = [{ code: "fr", label: "FR" }, { code: "es", label: "ES" }, { code: "en", label: "EN" }];

  return (
    <div className="min-h-screen bg-white" data-testid="ebook-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium" data-testid="ebook-back">
            <ArrowLeft size={16} />
            <img src={LOGO_URL} alt="Documatch" className="h-7 object-contain" style={{ background: "transparent" }} />
          </button>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            {langOptions.map(l => (
              <button key={l.code} onClick={() => { setLanguage(l.code); localStorage.setItem("documatch_language", l.code); }}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${language === l.code ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`ebook-lang-${l.code}`}>{l.label}</button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="pt-24 pb-16 px-4 bg-gradient-to-b from-slate-900 to-slate-800 text-white" data-testid="ebook-hero">
        <div className="max-w-4xl mx-auto text-center">
          <img src={LOGO_URL} alt="Documatch" className="h-16 object-contain mx-auto mb-6" style={{ background: "transparent" }} />
          <span className="inline-block bg-blue-600 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-wider">{c.badge}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">{c.title}<br /><span className="text-blue-400">{c.titleAccent}</span></h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {c.sections.map((sec, idx) => {
          const Icon = sec.icon;
          return (
            <Section key={sec.id}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Icon size={20} className="text-blue-600" /></div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900">{sec.title}</h2>
              </div>
              
              {sec.content && <p className="text-slate-600 leading-relaxed mb-6">{sec.content}</p>}

              {/* Stats grid */}
              {sec.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {sec.stats.map((s, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Bullets */}
              {sec.bullets && (
                <ul className="space-y-2 mb-6">
                  {sec.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <CheckCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />{b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Highlight */}
              {sec.highlight && <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg text-sm text-blue-800 mb-6">{sec.highlight}</div>}

              {/* Subsections */}
              {sec.subsections && sec.subsections.map((sub, si) => (
                <div key={si} className="mb-6">
                  <h3 className="font-semibold text-slate-800 mb-3">{sub.subtitle}</h3>
                  <ul className="space-y-1.5">
                    {sub.items.map((item, ii) => (
                      <li key={ii} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Table */}
              {sec.table && (
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-800 text-white">
                      <tr>{sec.table.headers.map((h, i) => <th key={i} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sec.table.rows.map((row, ri) => (
                        <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          {row.map((cell, ci) => <td key={ci} className={`px-4 py-2.5 ${ci === 0 ? "font-medium text-slate-800" : "text-slate-600"}`}>{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Cases */}
              {sec.cases && (
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {sec.cases.map((cs, ci) => (
                    <div key={ci} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                      <h4 className="font-semibold text-slate-900 mb-3 text-sm">{cs.name}</h4>
                      <div className="mb-3">
                        <p className="text-xs font-medium text-red-500 mb-1.5">{language === "fr" ? "Avant" : language === "es" ? "Antes" : "Before"}</p>
                        {cs.before.map((b, i) => <p key={i} className="text-xs text-slate-500 mb-0.5">- {b}</p>)}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-green-600 mb-1.5">{language === "fr" ? "Après" : language === "es" ? "Después" : "After"}</p>
                        {cs.after.map((a, i) => <p key={i} className="text-xs text-slate-700 mb-0.5">+ {a}</p>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Capabilities */}
              {sec.capabilities && (
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {sec.capabilities.map((cap, ci) => (
                    <div key={ci} className="bg-blue-50 rounded-xl p-5">
                      <h4 className="font-semibold text-blue-900 mb-2 text-sm">{cap.title}</h4>
                      <p className="text-xs text-blue-700">{cap.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Impact list */}
              {sec.impact && (
                <div className="flex flex-wrap gap-3 mb-6">
                  {sec.impact.map((imp, i) => (
                    <span key={i} className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-full">{imp}</span>
                  ))}
                </div>
              )}

              {/* Timeline */}
              {sec.timeline && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {sec.timeline.map((t, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                      <p className="text-xs text-slate-500">{t.period}</p>
                      <p className="text-lg font-bold text-blue-600">{t.value}</p>
                      <p className="text-xs text-slate-600">{t.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* KPIs */}
              {sec.kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {sec.kpis.map((k, i) => (
                    <div key={i} className="bg-blue-600 text-white rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold">{k.value}</p>
                      <p className="text-xs opacity-80">{k.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Decision criteria */}
              {sec.criteria && (
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {sec.criteria.map((cr, ci) => {
                    const CrIcon = cr.icon;
                    return (
                      <div key={ci} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                        <CrIcon size={20} className="text-blue-600 mt-0.5" />
                        <div><h4 className="font-semibold text-slate-900 text-sm">{cr.title}</h4><p className="text-xs text-slate-600">{cr.desc}</p></div>
                      </div>
                    );
                  })}
                </div>
              )}

              {sec.conclusion && <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-700 font-medium">{sec.conclusion}</div>}

              {/* CTA after section 4 */}
              {idx === 3 && (
                <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-center text-white">
                  <p className="font-semibold mb-3">{c.cta1}</p>
                  <Button onClick={() => navigate("/#analyzer")} className="bg-white text-blue-600 hover:bg-blue-50" data-testid="ebook-mid-cta">{c.cta3}</Button>
                </div>
              )}
            </Section>
          );
        })}

        {/* Why Documatch */}
        <Section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Building2 size={20} className="text-blue-600" /></div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">{c.whyTitle}</h2>
          </div>
          <p className="text-slate-600 mb-4">{c.whyContent}</p>
          <ul className="space-y-2 mb-6">
            {c.whyPoints.map((p, i) => <li key={i} className="flex items-center gap-2 text-slate-700"><CheckCircle size={16} className="text-blue-500" />{p}</li>)}
          </ul>
        </Section>

        {/* Conclusion */}
        <Section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl font-bold mb-4">{c.conclusionTitle}</h2>
          <p className="text-slate-300 leading-relaxed mb-8">{c.conclusionText}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/#analyzer")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ebook-final-cta1">
              <Sparkles size={16} className="mr-2" />{c.cta3}
            </Button>
            <Button variant="outline" onClick={() => navigate("/#contact")} className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="ebook-final-cta2">
              <MessageSquare size={16} className="mr-2" />{c.cta2}
            </Button>
          </div>
        </Section>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950 text-center">
        <img src={LOGO_URL} alt="Documatch" className="h-8 object-contain mx-auto mb-2" style={{ background: "transparent" }} />
        <p className="text-sm text-slate-500">Documatch &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
